import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LuxuryButton } from "./LuxuryButton";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1775133263714-848c8fe09e73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxTaGlyb2RoYXJhJTIwd2FybSUyMG9pbCUyMHBvdXIlMjBheXVydmVkYXxlbnwwfHx8fDE3OTEyMzg5MTB8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["Ancient wisdom,", "gently renewed,", "modern healing."];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.76, 0, 0.24, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex h-svh min-h-[640px] flex-col overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Shirodhara warm oil pour, an authentic Panchakarma therapy"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#2A241F]/55" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-background to-transparent" />

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1400px] flex-col justify-end px-6 pb-24 lg:px-12 lg:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="hero-overline"
          className="mb-8 text-[11px] font-medium uppercase tracking-[0.4em] text-[#E9B277]"
        >
          Ayurenew · Smart Panchakarma
        </motion.p>
        <h1
          data-testid="hero-heading"
          className="font-serif text-5xl font-light leading-[0.95] tracking-tight text-[#F9F6F0] sm:text-6xl lg:text-[5.5rem]"
        >
          {LINES.map((text, i) => (
            <span key={text} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "italic text-[#E9B277]" : ""}`}
              >
                {text}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          data-testid="hero-subtext"
          className="mt-8 max-w-xl text-base font-light leading-relaxed tracking-wide text-[#F9F6F0]/85 lg:text-lg"
        >
          Everybody is different. Your healing should be too.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <LuxuryButton data-testid="hero-cta-primary" onClick={() => scrollToId("#contact")}>
            Book a Discovery Call
          </LuxuryButton>
          <LuxuryButton data-testid="hero-cta-secondary" variant="light" onClick={() => scrollToId("#journey")}>
            Explore the Journey <ArrowDown size={14} />
          </LuxuryButton>
        </motion.div>
      </div>
    </section>
  );
};
